import React from 'react';
import {
  makeStyles,
  tokens,
  Card,
  CardHeader,
  Text,
  Title3,
  Subtitle2,
  mergeClasses,
  Select,
} from '@fluentui/react-components';
import { VerticalBarChart, GaugeChart } from '@fluentui/react-charting';
import { Folder32Regular, DataBarVertical32Regular, Trophy32Regular } from '@fluentui/react-icons';
import { useDashboard } from '../../hooks/useDashboard';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXL,
    padding: tokens.spacingHorizontalXXL,
    maxWidth: '1800px',
    margin: '0 auto',
    backgroundColor: tokens.colorNeutralBackground2,
    minHeight: '100vh',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    marginBottom: tokens.spacingVerticalL,
    padding: tokens.spacingVerticalXL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow4,
  },
  summaryCards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: tokens.spacingHorizontalL,
    marginBottom: tokens.spacingVerticalXXL,
  },
  summaryCard: {
    padding: tokens.spacingVerticalXXL,
    border: 'none',
    borderRadius: tokens.borderRadiusXLarge,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden',
    ':hover': {
      boxShadow: tokens.shadow16,
      transform: 'translateY(-4px)',
    },
    '::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '4px',
      background: `linear-gradient(90deg, ${tokens.colorBrandBackground}, ${tokens.colorBrandBackgroundPressed})`,
    },
  },
  summaryCardIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '72px',
    height: '72px',
    borderRadius: tokens.borderRadiusXLarge,
    fontSize: '32px',
    '& svg': {
      width: '32px',
      height: '32px',
    },
  },
  summaryCardIconProjects: {
    backgroundColor: tokens.colorPaletteBlueForeground2 + '20',
    color: tokens.colorPaletteBlueForeground2,
  },
  summaryCardIconTotal: {
    backgroundColor: tokens.colorPaletteGreenForeground1 + '20',
    color: tokens.colorPaletteGreenForeground1,
  },
  summaryCardIconMax: {
    backgroundColor: tokens.colorPaletteMarigoldForeground1 + '20',
    color: tokens.colorPaletteMarigoldForeground1,
  },
  summaryCardValue: {
    fontSize: tokens.fontSizeHero900,
    fontWeight: tokens.fontWeightBold,
    lineHeight: tokens.lineHeightHero900,
    marginBottom: tokens.spacingVerticalXS,
  },
  summaryCardLabel: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground2,
    letterSpacing: '0.02em',
  },
  summaryCardContent: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
    gap: tokens.spacingHorizontalL,
  },
  summaryCardTextContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    textAlign: 'right',
    flex: '1 1 auto',       
  },
  summaryCardIconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 auto',
  },
  chartGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalXL,
    marginBottom: tokens.spacingVerticalL,
  },
  gaugeGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: tokens.spacingHorizontalL,
    padding: tokens.spacingHorizontalL,
    '@media (max-width: 1200px)': {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
    '@media (max-width: 600px)': {
      gridTemplateColumns: '1fr',
    },
  },
  gaugeItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    transition: 'all 0.2s ease-in-out',
    minWidth: 0,
    ':hover': {
      boxShadow: tokens.shadow4,
      transform: 'translateY(-2px)',
    },
  },
  gaugeTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    textAlign: 'center',
    marginBottom: tokens.spacingVerticalS,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  gaugeCardContent: {
    width: '100%',
  },
  chartCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow2,
    overflow: 'hidden',
    transition: 'box-shadow 0.2s ease-in-out',
    width: '100%',
    ':hover': {
      boxShadow: tokens.shadow8,
    },
  },
  chartContainer: {
    padding: tokens.spacingHorizontalM,
    paddingBottom: tokens.spacingVerticalM,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflowX: 'auto',
  },
  chartHeaderWithFilter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: tokens.spacingHorizontalL,
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  projectSelector: {
    minWidth: '250px',
    maxWidth: '400px',
  },
});

const Dashboard: React.FC = React.memo(() => {
  const styles = useStyles();
  const { dashboard, fetchDashboard, dashboardLoading, dashboardError } = useDashboard();
  const [selectedProjectId, setSelectedProjectId] = React.useState<string>('todos');

  React.useEffect(() => {
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (dashboardLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <Title3>Dashboard de Projetos</Title3>
          <Subtitle2 style={{ color: tokens.colorNeutralForeground3 }}>
            Carregando dados...
          </Subtitle2>
        </div>
      </div>
    );
  }

  if (dashboardError) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <Title3>Dashboard de Projetos</Title3>
          <Subtitle2 style={{ color: tokens.colorPaletteRedForeground1 }}>
            Erro ao carregar dados: {dashboardError.message}
          </Subtitle2>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Title3>Dashboard de Projetos</Title3>
        <Subtitle2 style={{ color: tokens.colorNeutralForeground3 }}>
          Visão geral dos projetos e suas pontuações
        </Subtitle2>
      </div>

      {/* Cards de Resumo */}
      {dashboard?.resumo && (
        <div className={styles.summaryCards}>
          <Card className={styles.summaryCard}>
            <div className={styles.summaryCardContent}>
              <div className={styles.summaryCardIconWrapper}>
                <div className={mergeClasses(styles.summaryCardIcon, styles.summaryCardIconProjects)}>
                  <Folder32Regular />
                </div>
              </div>
              <div className={styles.summaryCardTextContent}>
                <Text className={styles.summaryCardValue} style={{ color: tokens.colorPaletteBlueForeground2 }}>
                  {dashboard.resumo.totalProjetos}
                </Text>
                <Text className={styles.summaryCardLabel}>
                  Total de Projetos
                </Text>
              </div>
            </div>
          </Card>

          <Card className={styles.summaryCard}>
            <div className={styles.summaryCardContent}>
              <div className={styles.summaryCardIconWrapper}>
                <div className={mergeClasses(styles.summaryCardIcon, styles.summaryCardIconTotal)}>
                  <DataBarVertical32Regular />
                </div>
              </div>
              <div className={styles.summaryCardTextContent}>
                <Text className={styles.summaryCardValue} style={{ color: tokens.colorPaletteGreenForeground1 }}>
                  {dashboard.resumo.pontuacaoTotalGeral.toFixed(1)}
                </Text>
                <Text className={styles.summaryCardLabel}>
                  Pontuação Alcançada
                </Text>
              </div>
            </div>
          </Card>

          <Card className={styles.summaryCard}>
            <div className={styles.summaryCardContent}>
              <div className={styles.summaryCardIconWrapper}>
                <div className={mergeClasses(styles.summaryCardIcon, styles.summaryCardIconMax)}>
                  <Trophy32Regular />
                </div>
              </div>
              <div className={styles.summaryCardTextContent}>
                <Text className={styles.summaryCardValue} style={{ color: tokens.colorPaletteMarigoldForeground1 }}>
                  {dashboard.resumo.pontuacaoMaximaGeral.toFixed(1)}
                </Text>
                <Text className={styles.summaryCardLabel}>
                  Pontuação Máxima
                </Text>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Gráficos */}
      <div className={styles.chartGrid}>
        {/* GaugeCharts: Pontuação por Projeto */}
        <Card className={styles.chartCard}>
          <CardHeader header={<Title3>Pontuação por Projeto</Title3>} />
          <div className={styles.gaugeGrid}>
            {dashboard?.projetos && dashboard.projetos.length > 0 ? (
              dashboard.projetos
                .filter(projeto => {
                  const max = Number(projeto.pontuacaoMaximaAtual) || 0;
                  return max > 0; // Apenas projetos com dados de extração
                })
                .slice(0, 9)
                .map((projeto, index) => {
                const current = Number(projeto.pontuacaoTotalAtual) || 0;
                const max = Number(projeto.pontuacaoMaximaAtual) || 0;
                const percentage = max > 0 ? (current / max) * 100 : 0;
                
                const getColor = (pct: number) => {
                  if (pct >= 80) return tokens.colorPaletteGreenForeground1;
                  if (pct >= 60) return tokens.colorPaletteYellowForeground3;
                  if (pct >= 40) return tokens.colorPaletteDarkOrangeForeground1;
                  return tokens.colorPaletteRedForeground1;
                };

                return (
                  <div key={`gauge-${index}`} className={styles.gaugeItem}>
                    <Text className={styles.gaugeTitle} title={projeto.descricao}>
                      #{projeto.id} - {projeto.descricao.length > 50
                        ? projeto.descricao.substring(0, 50) + '...'
                        : projeto.descricao}
                    </Text>
                    <GaugeChart
                      segments={[
                        {
                          size: current,
                          color: getColor(percentage),
                          legend: 'Atual',
                        },
                        {
                          size: max - current,
                          color: tokens.colorNeutralBackground4,
                          legend: 'Restante',
                        },
                      ]}
                      chartValue={current}
                      maxValue={max}
                      chartValueFormat={() => `${percentage.toFixed(0)}%`}
                      sublabel={`${current.toFixed(1)} / ${max.toFixed(1)}`}
                      width={180}
                      height={120}
                      roundCorners={true}
                      hideMinMax={true}
                      hideLegend={true}
                    />
                  </div>
                );
              })
            ) : (
              <Text>Sem dados de projetos disponíveis</Text>
            )}
          </div>
        </Card>

        {/* Gráfico de Barras: Evolução Geral das Pontuações ao Longo do Tempo */}
        <Card className={styles.chartCard}>
          <div className={styles.chartHeaderWithFilter}>
            <Title3>Evolução das Pontuações ao Longo do Tempo</Title3>
            <Select
              className={styles.projectSelector}
              value={selectedProjectId}
              onChange={(e, data) => setSelectedProjectId(data.value)}
            >
              <option value="todos">Todos os Projetos</option>
              {dashboard?.projetos && dashboard.projetos.map(projeto => (
                <option key={projeto.id} value={projeto.id.toString()}>
                  #{projeto.id} - {projeto.descricao}
                </option>
              ))}
            </Select>
          </div>
          <div className={styles.chartContainer}>
            {dashboard?.projetos && dashboard.projetos.length > 0 ? (
              (() => {
                // Filtrar projetos baseado na seleção
                const projetosFiltrados = selectedProjectId === 'todos'
                  ? dashboard.projetos
                  : dashboard.projetos.filter(p => p.id.toString() === selectedProjectId);

                // Coletar todas as evoluções dos projetos filtrados
                const allEvolutions = projetosFiltrados
                  .filter(projeto => projeto.evolucaoGeral && projeto.evolucaoGeral.length > 0)
                  .flatMap(projeto => projeto.evolucaoGeral.map(evo => ({
                    ...evo,
                    projetoDescricao: projeto.descricao
                  })));

                // Agrupar por data
                const dataMap = new Map<string, { total: number; max: number; count: number }>();
                allEvolutions.forEach(evo => {
                  const existing = dataMap.get(evo.data) || { total: 0, max: 0, count: 0 };
                  existing.total += Number(evo.pontuacaoTotal) || 0;
                  existing.max += Number(evo.pontuacaoMaxima) || 0;
                  existing.count += 1;
                  dataMap.set(evo.data, existing);
                });

                // Ordenar por data
                const sortedData = Array.from(dataMap.entries())
                  .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
                  .slice(-8); // Últimas 8 datas

                if (sortedData.length < 2) {
                  return <Text>Sem dados suficientes de evolução</Text>;
                }

                // Preparar dados para o VerticalBarChart
                const chartData = sortedData.map(([date, data]) => {
                  const formattedDate = new Date(date).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                  });
                  const yValue = Number(data.total) || 0;
                  const maxValue = Number(data.max) || 0;
                  return {
                    x: formattedDate,
                    y: yValue,
                    lineData: {
                      y: maxValue,
                      yAxisCalloutData: `Meta: ${maxValue.toFixed(1)}`,
                    },
                  };
                });

                return (
                  <VerticalBarChart
                    data={chartData}
                    width={320}
                    height={250}
                    barWidth={24}
                    colors={[tokens.colorPaletteGreenForeground1]}
                    lineLegendText="Meta"
                    lineLegendColor={tokens.colorPaletteBlueForeground2}
                    hideLegend={false}
                    hideTooltip={false}
                  />
                );
              })()
            ) : (
              <Text>Sem dados de evolução disponíveis</Text>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
});

export default Dashboard;
